import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axiosInstance from "../../../utils/axiosConfig";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DeleteIndents = () => {
  const [singleIndentId, setSingleIndentId] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [indentDetails, setIndentDetails] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [showRangeDeleteModal, setShowRangeDeleteModal] = useState(false);

  const Modal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-4 w-full max-w-sm">
          <h3 className="text-lg font-bold mb-3">{title}</h3>
          <p className="mb-4 text-gray-600 text-sm">{message}</p>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const fetchIndentDetails = async () => {
    try {
      const response = await axiosInstance.get(`/deleteindent/indents/${singleIndentId}`);
      setIndentDetails(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch indent details');
      setIndentDetails(null);
    }
  };

  const handleSingleDelete = async () => {
    try {
      await axiosInstance.delete(`/deleteindent/indents/${singleIndentId}`);
      
      setSuccess(`Indent ${singleIndentId} deleted successfully`);
      setIndentDetails(null);
      setSingleIndentId('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete indent');
    }
  };

  const handleRangeDelete = async () => {
    try {
      await axiosInstance.delete(`/deleteindent/indents/range`, {
        data: { startId: rangeStart, endId: rangeEnd }
      });
      
      setSuccess(`Indents from ${rangeStart} to ${rangeEnd} deleted successfully`);
      setRangeStart('');
      setRangeEnd('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete indents');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-grow p-4">
        <div className="bg-white shadow-md rounded-lg p-4 max-w-xl mx-auto">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Indents</h2>
          
          {/* Single Indent Delete Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Delete Single Indent</h3>
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 mb-3">
              <input
                type="number"
                value={singleIndentId}
                onChange={(e) => setSingleIndentId(e.target.value)}
                placeholder="Enter Indent ID"
                className="w-full p-2 border rounded text-sm"
              />
              <button
                onClick={fetchIndentDetails}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                Search
              </button>
            </div>

            {indentDetails && (
              <div className="border p-3 rounded mb-4 text-sm">
                <h4 className="font-semibold mb-2">Indent Details:</h4>
                <div className="space-y-1">
                  <p>Project: {indentDetails.ProjectName}</p>
                  <p>User: {indentDetails.UserName}</p>
                  <p>Created: {indentDetails.CreationDate}</p>
                  <p>Status: {indentDetails.Status}</p>
                </div>
                
                <button
                  onClick={() => setShowSingleDeleteModal(true)}
                  className="w-full mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
                >
                  Delete Indent
                </button>

                <Modal
                  isOpen={showSingleDeleteModal}
                  onClose={() => setShowSingleDeleteModal(false)}
                  onConfirm={handleSingleDelete}
                  title="Confirm Delete"
                  message={`Are you sure you want to delete Indent #${singleIndentId}? This action cannot be undone.`}
                />
              </div>
            )}
          </div>

          {/* Range Delete Section */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Delete Range of Indents</h3>
            <div className="flex flex-col space-y-2 mb-3">
              <input
                type="number"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                placeholder="Start Indent ID"
                className="w-full p-2 border rounded text-sm"
              />
              <input
                type="number"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                placeholder="End Indent ID"
                className="w-full p-2 border rounded text-sm"
              />
            </div>

            <button
              onClick={() => setShowRangeDeleteModal(true)}
              className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm"
              disabled={!rangeStart || !rangeEnd}
            >
              Delete Range
            </button>

            <Modal
              isOpen={showRangeDeleteModal}
              onClose={() => setShowRangeDeleteModal(false)}
              onConfirm={handleRangeDelete}
              title="Confirm Range Delete"
              message={`Are you sure you want to delete all indents from #${rangeStart} to #${rangeEnd}? This action cannot be undone.`}
            />
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 rounded text-sm">
              {success}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DeleteIndents;